import React from "react";
import { useNavigate } from "react-router";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TableSortLabel, Paper, Box,
} from "@mui/material";
import { ArtworkStub, ArtworkNavigationState } from "../../models/Artwork";
import type { ValuationType } from "../../models/SearchElements";
import ArtworkStatusBadge from "../ArtworkPreview/ArtworkStatusBadge";

// Maps a table column to the sortBy key(s) it drives. Columns without a `desc`
// entry only support ascending sort (matching the options already exposed via
// the card view's "Sort by" dropdown); columns with both support click-to-toggle.
const COLUMN_SORT_KEYS: Record<string, { asc: string; desc?: string }> = {
    artist: { asc: "artist", desc: "artist_desc" },
    title: { asc: "title", desc: "title_desc" },
    category: { asc: "category", desc: "category_desc" },
    location: { asc: "location", desc: "location_desc" },
    dateAcquired: { asc: "acquired_asc", desc: "acquired_desc" },
    value: { asc: "value_asc", desc: "value_desc" },
};

type ArtworkTableViewProps = {
    artworks: ArtworkStub[];
    showValues: boolean;
    valueViewOption: ValuationType;
    currency: string;
    conversionRates: Record<string, number>;
    sortBy: string;
    onColumnSort: (columnKey: keyof typeof COLUMN_SORT_KEYS) => void;
    breadcrumb: (categoryId: number) => string;
    getCategoryNameById: (id: number) => string | undefined;
};

function SortableHeaderCell({
    label,
    columnKey,
    sortBy,
    onColumnSort,
    align = "left",
}: {
    label: string;
    columnKey: keyof typeof COLUMN_SORT_KEYS;
    sortBy: string;
    onColumnSort: (columnKey: keyof typeof COLUMN_SORT_KEYS) => void;
    align?: "left" | "right";
}) {
    const keys = COLUMN_SORT_KEYS[columnKey];
    const isActive = sortBy === keys.asc || sortBy === keys.desc;
    const direction: "asc" | "desc" = sortBy === keys.desc ? "desc" : "asc";
    return (
        <TableCell align={align} sortDirection={isActive ? direction : false}>
            <TableSortLabel
                active={isActive}
                direction={direction}
                onClick={() => onColumnSort(columnKey)}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );
}

const ArtworkTableView: React.FC<ArtworkTableViewProps> = ({
    artworks,
    showValues,
    valueViewOption,
    currency,
    conversionRates,
    sortBy,
    onColumnSort,
    breadcrumb,
    getCategoryNameById,
}) => {
    const navigate = useNavigate();

    const getRowValue = (art: ArtworkStub): { amount: number; currencyCode: string } => {
        switch (valueViewOption) {
            case "fairMarketValue":
                return {
                    amount: art.fairMarketValuation?.lowerRangeAmount ?? 0,
                    currencyCode: art.fairMarketValuation?.currencyCode ?? art.currencyCode,
                };
            case "insurance":
                return {
                    amount: art.insuranceValuation?.lowerRangeAmount ?? 0,
                    currencyCode: art.insuranceValuation?.currencyCode ?? art.currencyCode,
                };
            default:
                return {
                    amount: art.acquisitionValuation?.lowerRangeAmount ?? 0,
                    currencyCode: art.acquisitionValuation?.currencyCode ?? art.currencyCode,
                };
        }
    };

    const goToArtwork = (art: ArtworkStub, index: number) => {
        navigate(`/artwork/${art.artworkId}`, {
            state: {
                source: "gallery",
                artworks,
                currentIndex: index,
            } satisfies ArtworkNavigationState,
        });
    };

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 60 }} />
                        <TableCell sx={{ width: 44 }}>Status</TableCell>
                        <SortableHeaderCell label="Artist" columnKey="artist" sortBy={sortBy} onColumnSort={onColumnSort} />
                        <SortableHeaderCell label="Title" columnKey="title" sortBy={sortBy} onColumnSort={onColumnSort} />
                        <SortableHeaderCell label="Category" columnKey="category" sortBy={sortBy} onColumnSort={onColumnSort} />
                        <SortableHeaderCell label="Location" columnKey="location" sortBy={sortBy} onColumnSort={onColumnSort} />
                        <SortableHeaderCell label="Date Acquired" columnKey="dateAcquired" sortBy={sortBy} onColumnSort={onColumnSort} />
                        {showValues && (
                            <SortableHeaderCell label="Value" columnKey="value" sortBy={sortBy} onColumnSort={onColumnSort} align="right" />
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {artworks.map((art, index) => {
                        const { amount, currencyCode } = getRowValue(art);
                        const fromRate = conversionRates[currencyCode] || 1;
                        const toRate = conversionRates[currency] || 1;
                        const convertedValue = (amount / fromRate) * toRate;
                        const acquiredDate = art.dateAcquired
                            ? new Date(art.dateAcquired).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                            })
                            : "—";
                        return (
                            <TableRow
                                key={art.artworkId}
                                hover
                                className="gallery-table-row"
                                onClick={() => goToArtwork(art, index)}
                            >
                                <TableCell>
                                    {art.imageUrl ? (
                                        <img src={art.imageUrl} alt={art.artWorkTitle} className="gallery-table-thumb" />
                                    ) : (
                                        <div className="gallery-table-thumb-placeholder">🖼️</div>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <ArtworkStatusBadge statusId={art.artworkStatusId} size={22} overlay={false} />
                                </TableCell>
                                <TableCell title={art.artistName}>{art.artistName}</TableCell>
                                <TableCell title={art.artWorkTitle}>{art.artWorkTitle || "Untitled"}</TableCell>
                                <TableCell title={breadcrumb(art.categoryId)}>
                                    {getCategoryNameById(art.categoryId) ?? art.categoryName}
                                </TableCell>
                                <TableCell title={art.locationPath}>{art.locationPath}</TableCell>
                                <TableCell>{acquiredDate}</TableCell>
                                {showValues && (
                                    <TableCell align="right">
                                        <Box>
                                            {amount.toLocaleString()} {currencyCode}
                                        </Box>
                                        <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                            ≈ {convertedValue.toLocaleString(undefined, { maximumFractionDigits: 2 })} {currency}
                                        </Box>
                                    </TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ArtworkTableView;
